import { useState, useEffect, useCallback } from "react"
import { Link, useSearchParams } from "react-router-dom"
import Swal from "sweetalert2"
import {
  getProperties,
  getAllOwnersWithProperties,
  deactivateProperty,
} from "../../actions/property-actions"
import Paginator from "../../components/paginator/paginator"
import TableComponent from "../../components/table/table"
import {
  propertiesTableBody,
  propertiesTableHeaders,
} from "./create-property-data"
import { alert } from "../../components/Alert"
import { useApp } from "../../context/useApp"
import PropertiesFilters from "./PropertiesFilters"

function PropertiesList() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [properties, setProperties] = useState([])
  const [loadData, setLoadData] = useState(true)
  const [isLoadingTable, setIsLoadingTable] = useState(false)
  const { user, setLoading } = useApp()
  const currentLink = "/inmuebles"

  // Estados para búsqueda y filtros
  const [filters, setFilters] = useState({
    code: searchParams.get("code") || "",
    owner: searchParams.get("owner") || "",
    propertyType: searchParams.get("propertyType") || "",
    status: searchParams.get("status") || "",
    department: searchParams.get("department") || "",
    city: searchParams.get("city") || "",
    address: searchParams.get("address") || "",
    neighborhood: searchParams.get("neighborhood") || "",
  })

  // Paginación
  const [paginatorData, setPaginatorData] = useState({
    totalitems: 0,
    totalpages: 0,
    currentpage: 0,
  })
  const [skip, setSkip] = useState(
    parseInt(searchParams.get("page"))
      ? (parseInt(searchParams.get("page")) - 1) * 10
      : 0,
  )

  // Opciones disponibles para los filtros
  const [availableOptions, setAvailableOptions] = useState({
    owners: [],
    cities: [],
    neighborhoods: [],
  })

  // Cargar propiedades con filtros
  const loadProperties = useCallback(async () => {
    try {
      setIsLoadingTable(true)
      let response
      const params = {
        skip,
        limit: 10,
        ...(filters.code && { code: filters.code }),
        ...(filters.owner && { owner: filters.owner }),
        ...(filters.propertyType && { propertyType: filters.propertyType }),
        ...(filters.status && { status: filters.status }),
        ...(filters.department && { province: filters.department }),
        ...(filters.city && { city: filters.city }),
        ...(filters.address && { address: filters.address }),
        ...(filters.neighborhood && { neighborhood: filters.neighborhood }),
      }

      response = await getProperties(skip, setLoading, user.token, params)

      let data = response.data
      if (data.code === "01") {
        alert("Error", data.message)
        return
      }

      // Procesar propiedades
      const processedProperties = data.data.map((property) => {
        property.statusText = "Sin alquilar"
        property.ubication = `${property.address} B/ ${property.neighborhood}\nTipo: ${property.propertyType}, ${property.city} - ${property.province}`
        
        // Manejar tanto array (de $lookup) como objeto (de populate)
        const ownerData = Array.isArray(property.owner.userID) 
          ? property.owner.userID[0] 
          : property.owner.userID
        property.ownerText = `${ownerData?.firstName || ""} ${ownerData?.lastName || ""}`.trim()
        
        property.infoText =
          property.neighborhood !== "PENDIENTE" ? "Completa" : "Sin revisar"
        property.alertClass =
          property.neighborhood === "PENDIENTE" ? true : false
        if (property.alertClass) property.alertItem = "infoText"
        // Agregar link de edición usando el _id de MongoDB
        property.editLink = `${currentLink}/${property._id}/editar`

        const request = property.requests.find(
          (request) =>
            request.status === "Aprobado" || request.status === "En contrato",
        )

        if (request) {
          const contract = request.contracts.find(
            (contract) => contract.status === "Activo",
          )
          if (contract) {
            property.statusText = "En contrato"
          }
        }
        return property
      })

      setProperties(processedProperties)

      setPaginatorData({
        totalitems: response.headers?.totalitems || 0,
        totalpages: response.headers?.totalpages || 0,
        currentpage: response.headers?.currentpage || 1,
      })
    } catch (error) {
      console.error("Error loading properties:", error)
    } finally {
      setLoading(false)
      setIsLoadingTable(false)
    }
  }, [skip, filters, user.token, setLoading])

  // Cargar todos los propietarios disponibles
  const loadAvailableOptions = useCallback(async () => {
    try {
      const owners = await getAllOwnersWithProperties(user.token)
      setAvailableOptions({
        owners: owners,
        cities: [],
        neighborhoods: [],
      })
    } catch (error) {
      console.error("Error loading available options:", error)
    }
  }, [user.token])

  useEffect(() => {
    if (loadData && user?.token) {
      setLoadData(false)
      loadProperties()
    }
  }, [loadData, user?.token, loadProperties])

  useEffect(() => {
    if (user?.token) {
      loadAvailableOptions()
    }
  }, [user?.token, loadAvailableOptions])

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
    setSkip(0)

    // Actualizar URL params
    const params = new URLSearchParams()
    if (newFilters.code) params.set("code", newFilters.code)
    if (newFilters.owner) params.set("owner", newFilters.owner)
    if (newFilters.propertyType)
      params.set("propertyType", newFilters.propertyType)
    if (newFilters.status) params.set("status", newFilters.status)
    if (newFilters.department) params.set("department", newFilters.department)
    if (newFilters.city) params.set("city", newFilters.city)
    if (newFilters.address) params.set("address", newFilters.address)
    if (newFilters.neighborhood)
      params.set("neighborhood", newFilters.neighborhood)

    setSearchParams(params.toString() ? `?${params.toString()}` : "")
    setLoadData(true)
  }

  const handleClearFilters = () => {
    setFilters({
      code: "",
      owner: "",
      propertyType: "",
      status: "",
      department: "",
      city: "",
      address: "",
      neighborhood: "",
    })
    setSkip(0)
    setSearchParams("")
    setLoadData(true)
  }

  const handlePageChange = (newSkip) => {
    setSkip(newSkip)
    const page = Math.floor(newSkip / 10) + 1

    const params = new URLSearchParams()
    params.set("page", page)
    Object.entries(filters).forEach(([key, val]) => {
      if (val) params.set(key, val)
    })
    setSearchParams(params.toString() ? `?${params.toString()}` : "")
    setLoadData(true)
  }

  const handleDeleteProperty = async (property) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: `¿Deseas desactivar la propiedad ${property.code}? Esta acción no se puede deshacer.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#76b4ff",
      confirmButtonText: "Sí, desactivar",
      cancelButtonText: "Cancelar",
    })

    if (result.isConfirmed) {
      setLoading(true)
      const success = await deactivateProperty(property._id, user.token)
      setLoading(false)

      if (success) {
        setLoadData(true)
      }
    }
  }

  return (
    <div className="w-100 d-flex">
      <div className="container d-flex flex-column">
        <h1 className="my-5">Inmuebles</h1>
        <div className="d-flex justify-content-end mb-3">
          <Link to={`${currentLink}/nuevo`} className="primary-button">
            Añadir inmueble +
          </Link>
        </div>

        {/* Componente de Filtros Modernos */}
        <PropertiesFilters
          currentFilters={filters}
          onFiltersChange={handleFilterChange}
          availableOptions={availableOptions}
          onClearFilters={handleClearFilters}
        />

        {/* Info de resultados */}
        <div className="results-info">
          <span>
            Inmuebles: <b>{paginatorData.totalitems}</b>
          </span>
          {Object.values(filters).some((f) => f !== "") && (
            <span className="filters-applied">
              ({Object.values(filters).filter((f) => f !== "").length} filtros
              aplicados)
            </span>
          )}
        </div>

        {/* Tabla de propiedades */}
        <TableComponent
          id={"properties-table"}
          headers={propertiesTableHeaders}
          body={propertiesTableBody}
          skip={skip}
          data={properties}
          link={``}
          hasOwnLink={true}
          needOptions={true}
          deleteAction={handleDeleteProperty}
          isLoading={isLoadingTable}
        />

        {/* Paginador */}
        {paginatorData.totalpages > 1 && (
          <Paginator
            skip={skip}
            data={paginatorData}
            setSkip={handlePageChange}
            setLoadData={(need) => setLoadData(need)}
            queryParams={searchParams}
            setQueryParams={setSearchParams}
          />
        )}
      </div>
    </div>
  )
}

export default PropertiesList
